import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, CircleNotch, User, Briefcase, MapPin } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CURRENCIES as ISO } from "@/lib/currencies";
import { currencySymbol } from "@/lib/format";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";

const NAME = Object.fromEntries(ISO);
const POPULAR = ["ZAR", "USD", "EUR", "GBP", "NGN", "KES"];
const TRACK = ["Overall spending", "Groceries", "Household", "Restaurants", "Fuel", "Clothing", "Electronics", "Subscriptions", "Other"];
const STEPS = 4;

export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState("personal");
  const [currency, setCurrency] = useState("ZAR");
  const [detected, setDetected] = useState(null); // { country, currency }
  const [tracked, setTracked] = useState(["Overall spending", "Groceries"]);
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  // Detect the user's country/currency from their IP (via our backend to avoid CORS); fall back to ZAR.
  useEffect(() => {
    let cancelled = false;
    api.get("/geo")
      .then(({ data: d }) => {
        if (cancelled || !d) return;
        const cur = (d.currency || "").toUpperCase();
        const country = d.country_name || d.country_code || null;
        if (country || (cur && NAME[cur])) {
          setDetected({ country, currency: cur && NAME[cur] ? cur : null });
        }
        if (cur && NAME[cur]) setCurrency(cur);
      })
      .catch(() => { /* keep ZAR default */ });
    return () => { cancelled = true; };
  }, []);

  const toggle = (c) => setTracked((t) => (t.includes(c) ? t.filter((x) => x !== c) : [...t, c]));

  const quick = [...new Set([currency, ...POPULAR])].filter((c) => NAME[c]).slice(0, 6);

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding", {
        account_type: accountType, currency, country: detected?.country || null,
        tracked_categories: tracked, monthly_budget: budget ? Number(budget) : null,
      });
      await refresh();
      toast.success("You're all set!");
      navigate("/");
    } catch {
      toast.error("Could not complete setup");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-riq-bg flex flex-col">
      <div className="flex items-center justify-between px-6 py-5 max-w-2xl mx-auto w-full">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-riq-primary flex items-center justify-center p-2"><img src="/sliply-mark.png" alt="Sliply" className="w-full h-full object-contain" /></div>
          <span className="font-head text-lg font-bold text-riq-primary">Sliply</span>
        </div>
        <div className="flex gap-1.5">
          {Array.from({ length: STEPS }, (_, i) => i + 1).map((s) => <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? "w-8 bg-riq-primary" : "w-4 bg-riq-border"}`} />)}
        </div>
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-6 pb-12">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              {step === 1 && (
                <div>
                  <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">How will you use Sliply?</h1>
                  <p className="text-muted-foreground mt-2 mb-8">Pick a profile — you can change it later in Settings.</p>
                  <div className="space-y-3">
                    <button data-testid="onboarding-account-personal" onClick={() => setAccountType("personal")}
                      className={`w-full flex items-start gap-4 rounded-2xl border p-5 text-left transition-all ${accountType === "personal" ? "border-riq-primary bg-riq-sage/40" : "border-riq-border hover:border-neutral-300"}`}>
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accountType === "personal" ? "bg-riq-primary text-white" : "bg-riq-primary/10 text-riq-primary"}`}><User size={22} weight="duotone" /></span>
                      <span className="flex-1">
                        <span className="flex items-center gap-2 font-head font-bold text-riq-primary">Personal {accountType === "personal" && <Check size={16} weight="bold" className="text-riq-primary" />}</span>
                        <span className="block text-sm text-muted-foreground mt-0.5">Track household spending, budgets, warranties & holidays.</span>
                      </span>
                    </button>
                    <button data-testid="onboarding-account-business" onClick={() => setAccountType("business")}
                      className={`w-full flex items-start gap-4 rounded-2xl border p-5 text-left transition-all ${accountType === "business" ? "border-riq-primary bg-riq-sage/40" : "border-riq-border hover:border-neutral-300"}`}>
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accountType === "business" ? "bg-riq-primary text-white" : "bg-riq-primary/10 text-riq-primary"}`}><Briefcase size={22} weight="duotone" /></span>
                      <span className="flex-1">
                        <span className="flex items-center gap-2 font-head font-bold text-riq-primary">Business {accountType === "business" && <Check size={16} weight="bold" className="text-riq-primary" />}</span>
                        <span className="block text-sm text-muted-foreground mt-0.5">Everything in Personal, plus trips, expense descriptions & VAT-friendly PDF reports.</span>
                      </span>
                    </button>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div>
                  <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">What currency do you use?</h1>
                  <p className="text-muted-foreground mt-2 mb-4">This is used across your dashboards and receipts.</p>
                  {detected?.country && (
                    <div data-testid="currency-detected" className="mb-5 inline-flex items-center gap-2 rounded-full bg-riq-sage/60 text-riq-primary px-3.5 py-1.5 text-xs font-semibold">
                      <MapPin size={14} weight="fill" /> Detected from {detected.country}{detected.currency ? `: ${detected.currency}` : ""}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {quick.map((code) => (
                      <button key={code} data-testid={`currency-${code}`} onClick={() => setCurrency(code)}
                        className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-all ${currency === code ? "border-riq-primary bg-riq-sage/40" : "border-riq-border hover:border-neutral-300"}`}>
                        <span className="w-10 h-10 rounded-xl bg-riq-primary/10 flex items-center justify-center font-head font-bold text-riq-primary">{currencySymbol(code).trim() || code}</span>
                        <span className="min-w-0"><span className="block font-medium text-sm">{code}</span><span className="block text-xs text-muted-foreground truncate">{NAME[code]}</span></span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-4">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Use a different currency</span>
                    <div className="mt-1.5"><CurrencyCombobox value={currency} onChange={setCurrency} testId="onboarding-currency" /></div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div>
                  <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">What do you want to track?</h1>
                  <p className="text-muted-foreground mt-2 mb-8">Pick the areas you care about. You can change this later.</p>
                  <div className="flex flex-wrap gap-2.5">
                    {TRACK.map((c) => (
                      <button key={c} data-testid={`track-${c.replace(/\s/g, "-").toLowerCase()}`} onClick={() => toggle(c)}
                        className={`rounded-full px-4 py-2.5 text-sm font-medium border transition-all ${tracked.includes(c) ? "bg-riq-primary text-white border-riq-primary" : "bg-white border-riq-border text-neutral-600 hover:border-neutral-300"}`}>
                        {tracked.includes(c) && <Check size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />}{c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {step === 4 && (
                <div>
                  <h1 className="font-head text-3xl font-bold tracking-tight text-riq-primary">Set a monthly budget</h1>
                  <p className="text-muted-foreground mt-2 mb-8">Optional — we'll track your spending against it. You can skip this.</p>
                  <div className="relative">
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl font-head font-semibold text-muted-foreground">{currencySymbol(currency).trim()}</span>
                    <input data-testid="budget-input" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="15,000"
                      className="w-full bg-secondary/60 rounded-2xl border border-transparent focus:border-riq-primary focus:ring-2 focus:ring-riq-primary/20 pl-14 pr-5 py-5 text-2xl font-head font-semibold outline-none" />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {[5000, 10000, 15000, 20000].map((v) => (
                      <button key={v} onClick={() => setBudget(String(v))} className="rounded-full bg-white border border-riq-border px-4 py-2 text-sm hover:border-riq-primary">{currencySymbol(currency).trim()}{v.toLocaleString()}</button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-10">
            {step > 1 ? (
              <button data-testid="onboarding-back" onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-neutral-500 font-medium"><ArrowLeft size={18} /> Back</button>
            ) : <div />}
            <div className="flex items-center gap-3">
              {step === STEPS && <button data-testid="onboarding-skip" onClick={finish} className="text-neutral-500 font-medium">Skip</button>}
              {step < STEPS ? (
                <button data-testid="onboarding-next" onClick={() => setStep(step + 1)} className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-7 py-3 font-medium hover:-translate-y-0.5 transition-transform">Continue <ArrowRight size={18} weight="bold" /></button>
              ) : (
                <button data-testid="onboarding-finish" disabled={saving} onClick={finish} className="flex items-center gap-2 rounded-full bg-riq-primary text-white px-7 py-3 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">{saving ? <CircleNotch className="animate-spin" size={18} /> : <>Get started <ArrowRight size={18} weight="bold" /></>}</button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
